import { EvidenceMetadata } from './evidences';
import { GeoPoint } from './geoModels';
import { ChannelId } from './gptTypes';
import { MediaFileType, MediaOrigin } from './media';

export interface StreamingCaptureChannelResponse {
  key?: string;
  name?: string;
  error?: string;
}

export type ChannelKey = `channel_${number}`;

export type StreamingCaptureResponse = {
  [K in ChannelKey]?: StreamingCaptureChannelResponse;
};

export interface StreamingTimelineRecord {
  uuid: string;
  startPoint: GeoPoint;
  endPoint: GeoPoint;
  startReference: string;
  endReference: string;
  speed: number;
  distance: number;
  duration: number;
  encodedPolyline: string;
  startTime: number;
  endTime: number;
  events?: StreamingTimelineEvent[];
}

export type StreamingTimeline = StreamingTimelineRecord[];

export interface StreamingTimelineEvent {
  created: number;
  latitude: number;
  longitude: number;
  mediaOrigin: MediaOrigin;
  alarmType?: number;
  fileType?: MediaFileType;
  geoReference?: string;
  key?: string;
  metadata: EvidenceMetadata;
}

// Model for the timeline component
export interface TimelineRecord extends StreamingTimelineRecord {
  startLabel: string;
  endLabel: string;
  lineStringCoordinates?: number[][];
}

export type Timeline = TimelineRecord[];

export enum TimelineSource {
  device = 'device',
  unavailable = 'unavailable',
}

export interface TimelineAvailability {
  source: TimelineSource;
  channels?: {
    [K in ChannelKey]: Record<StreamType, TimelineBracket[]>;
  };
}

export interface TimelineBracket {
  begin: number;
  end: number;
  locked: boolean;
  truncatedStart: boolean;
  truncatedEnd: boolean;
}

export enum StreamingMenuActions {
  video = 'video',
  live = 'live',
  capture = 'capture',
}
export type TimelineChangeEvent = {
  start: number;
  end: number;
};

export type ZoomLevel = {
  label: string;
  durationMinutes: number;
  staticDuration: number;
};

export enum CaptureDuration {
  fiveSeconds = 0,
  tenSeconds,
  thirtySeconds,
  oneMinute,
  fiveMinutes,
}

export enum CaptureMode {
  requesting = 1,
  playback,
}

export enum StreamingTimezone {
  past = 1,
  current,
  future,
  outOfZone,
}

export enum StreamType {
  main = 'main',
  sub = 'sub',
}

export enum MediaStream {
  hls = 'hls',
  flv = 'flv',
}

export enum StreamingCommand {
  startVideostream = 'start_videostream',
  takePhoto = 'take_photo',
  playbackVideo = 'playback_video',
  requestVideo = 'request_video',
  requestPhoto = 'request_photo',
}

export interface StreamingCommandResponse {
  commandId: number;
  flespiResponse: {
    result: FlespiItem[];
  };
}

export interface FlespiItem {
  timestamp: number;
  meta: any;
  response: FlespiItemResponse;
  id: number;
  command_id: number;
  max_attempts: number;
  name: StreamingCommand;
  device_id: number;
  executed: boolean;
  properties: FlespiItemProperties;
  priority: number;
  condition: string;
  expires: number;
}

export interface FlespiItemResponse {
  url: string;
  uuid: string;
  channel?: ChannelId;
  command_id?: number;
  connection_id?: number;
  has_audio?: boolean;
  height?: number;
  mediastream?: MediaStream;
  video_codec?: string;
  width?: number;
  created?: number;
  mime?: string;
  name?: string;
  size?: number;
  meta?: {
    channel: ChannelId;
    command_id: number;
    height: number;
    type: string;
    width: number;
  };
}

export interface FlespiItemProperties {
  audio: boolean;
  channel: ChannelId;
  mediastream: MediaStream;
  streamtype: StreamType;
}

export enum StreamingState {
  ok,
  stall, // Paused
  stuck, // Blocked or Stopped
}
